import { clsx } from '@alexis/helpers/clsx';
import { getCookie } from '@alexis/helpers/cookie';
import { genzoTrackActionEvent } from '@alexis/helpers/genzo';
import { generateCUID } from '@alexis/helpers/identity';
import axios, { CancelToken, CancelTokenSource } from 'axios';
import { clamp } from 'lodash-es';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { getGuestReviews } from '@apis/hotel';

import { countryCodeToFlagEmoji, postedDateDisplay } from '@helpers/hotel';
import { cloudinaryImageTransformations } from '@helpers/imageTransformations';

import ChevronRightIcon from '@icons/chevron-right-big.svg';
import ChevronLeftIcon from '@icons/chevron_left_big.svg';
import EmptyReviewsIcon from '@icons/hotel_empty.svg';
import PeopleIcon from '@icons/people.svg';
import DisSatisfiedIcon from '@icons/sentiment_dissatisfied.svg';
import SatisfiedIcon from '@icons/sentiment_satisfied.svg';

import useInView from '@hooks/useInView';
import { useReviewRatingType } from '@hooks/useReviewRatingType';

import { getLocaleState, getTranslationsState } from '@redux/selectors';

import { HotelGuestReview, REVIEW_GUEST_TYPE } from '@wegoTypes/hotels/hotelGuestReview';

import FilterReviews from '../FilterReviews';
import { ReviewsFilters } from '../FilterReviews/FilterReviews';
import styles from './GuestReviews.module.scss';

const GuestReviews: React.FC<{
  reviews: HotelGuestReview;
  reviewLocale: string;
}> = ({ reviews, reviewLocale }) => {
  const apiBaseUrl = API_BASE_URL;
  const locale = useSelector(getLocaleState);
  const translations = useSelector(getTranslationsState);
  const { getReviewRatingTypeColorClassName } = useReviewRatingType();

  const params = useParams<{
    hotelId: string;
  }>();

  const [guestReviews, setGuestReviews] = useState<HotelGuestReview>();
  const [currentPage, setCurrentPage] = useState(1);
  const [isInitial, setIsInitial] = useState(true);
  const [reviewFilters, setReviewFilters] = useState<ReviewsFilters>({
    sortBy: ['posted_at,desc'],
    languageSelector: reviewLocale,
    guestType: REVIEW_GUEST_TYPE.all_guests,
    queries: '',
    keyPhrase: [],
  });
  const [hasTriggeredGenzoAction, setHasTriggeredGenzoAction] = useState(false);
  const { ref: guestReviewsRef, isInView: isGuestReviewShown } = useInView({ threshold: 0.3 });

  const pageViewId = useMemo<string>(() => {
    return generateCUID();
  }, []);

  const pager = useCallback((currentPage: number, pageCount: number): Array<number | '...'> => {
    const size = pageCount > 7 ? 7 : pageCount;
    const offset = (size - 1) / 2;
    const shouldAddDots = pageCount > size;

    const rangeConfig = {
      start: clamp(currentPage - offset, 1, shouldAddDots ? pageCount - size + 1 : 1),
      end: clamp(currentPage + offset, size, pageCount),
    };

    const rangeLength = rangeConfig.end - rangeConfig.start + 1;
    const pages: Array<number | '...'> = Array(rangeLength)
      .fill(0)
      .map((_, i) => i + rangeConfig.start);

    if (shouldAddDots && pages[0] !== 1) {
      pages[0] = 1;
      pages[1] = '...';
    }
    if (shouldAddDots && pages[pages.length - 1] !== pageCount) {
      pages[pages.length - 1] = pageCount;
      pages[pages.length - 2] = '...';
    }
    return pages;
  }, []);

  useEffect(() => {
    setIsInitial(false);
  }, []);

  useEffect(() => {
    setGuestReviews(reviews);
  }, [reviews]);

  // Get guest reviews
  useEffect(() => {
    if (params.hotelId && !isInitial) {
      const cancelTokenSource: CancelTokenSource = axios.CancelToken.source();
      const combinedQueries: string[] = [
        ...(reviewFilters.queries ? [reviewFilters.queries] : []),
        ...(reviewFilters.keyPhrase || []),
      ];

      const initGuestReviews = async (
        hotelId: number,
        cancelToken: CancelToken,
        localeToUse?: string,
      ): Promise<void> => {
        try {
          const guestReviews = await getGuestReviews(
            apiBaseUrl,
            hotelId,
            {
              ...(localeToUse?.length ? { locale: localeToUse } : {}),
              per_page: 5,
              page: currentPage,
              sorts: reviewFilters.sortBy,
              ...(!reviewFilters.guestType ? {} : { guest_type: reviewFilters.guestType }),
              ...(combinedQueries.length ? { queries: combinedQueries } : {}),
            },
            cancelToken,
          );

          setGuestReviews(guestReviews);
        } catch (_) {}
      };

      initGuestReviews(
        parseInt(params.hotelId!),
        cancelTokenSource.token,
        reviewFilters.languageSelector,
      );

      return () => {
        cancelTokenSource.cancel('Component unmount.');
      };
    }
  }, [params.hotelId, currentPage, locale, reviewFilters]);

  const handledGenzoActionEvent = useCallback((): void => {
    const wegoAnalyticsClientIdCookie = getCookie('wego_analytics_client_id');
    const wegoAnalyticsClientSessionIdCookie = getCookie('wego_analytics_client_session_id');
    if (!!wegoAnalyticsClientIdCookie && !!wegoAnalyticsClientSessionIdCookie) {
      const eventData = {
        id: pageViewId,
        category: 'view_guest_review_section_hoteldetails_dweb',
        object: 'hoteldetails_page',
        action: 'scroll',
        value: '',
      };
      genzoTrackActionEvent(
        apiBaseUrl,
        wegoAnalyticsClientIdCookie,
        wegoAnalyticsClientSessionIdCookie,
        undefined,
        eventData,
      );
    }
  }, [apiBaseUrl, pageViewId]);

  useEffect(() => {
    if (isGuestReviewShown && !hasTriggeredGenzoAction) {
      handledGenzoActionEvent();
      setHasTriggeredGenzoAction(true);
    }
  }, [isGuestReviewShown, guestReviewsRef, hasTriggeredGenzoAction]);

  const highlightText = useCallback(
    (text: string) => {
      if (!guestReviews || !guestReviews?.meta?.hightlights?.length) {
        return text;
      }

      guestReviews?.meta?.hightlights.forEach((match) => {
        text = match ? text.replace(new RegExp(`(${match})`, 'gi'), `<mark>${match}</mark>`) : text;
      });
      return text;
    },
    [guestReviews],
  );

  return (
    <div ref={guestReviewsRef} className={styles.container}>
      <div id='GuestReviews' className={styles.guestReviewHeader}>
        {translations.guest_reviews}
      </div>

      <FilterReviews
        filters={reviewFilters}
        setFilters={setReviewFilters}
        setCurrentPage={setCurrentPage}
      />

      {guestReviews?.data?.length ? (
        <>
          {guestReviews.data.map((review, index) => (
            <div key={`${review.title}-${index}`} className={styles.review}>
              <div className={styles.reviewInformation}>
                <div
                  className={clsx(
                    styles.reviewScore,
                    getReviewRatingTypeColorClassName(review.rating * 10, styles),
                  )}
                >
                  {parseFloat(String(review.rating)).toFixed(review.rating < 10 ? 1 : 0)}
                </div>

                <div className={styles.author}>
                  <div className={styles.authorName}>{review.author.name}</div>
                  {review.author.country ? (
                    <div className={styles.authorDetails}>
                      {countryCodeToFlagEmoji(review.author.country.code)}&nbsp;
                      <span className={styles.detail}>{review.author.country.name}</span>
                    </div>
                  ) : null}

                  <div className={styles.authorDetails}>
                    <PeopleIcon width='18px' />
                    <span className={styles.detail}>{review.author.type.replaceAll('_', ' ')}</span>
                  </div>
                </div>

                <div className={styles.contentContainer}>
                  <div>
                    {review.title ? (
                      <div
                        className={styles.reviewTitle}
                        dangerouslySetInnerHTML={{
                          __html: highlightText(review.title),
                        }}
                      />
                    ) : null}

                    {!!review.contents ? (
                      review.contents.map((content) => {
                        return (
                          <div key={content.comment} className={styles.comment}>
                            {content.type === 'pros' ? (
                              <SatisfiedIcon className={styles.satisfied} />
                            ) : null}
                            {content.type === 'cons' ? (
                              <DisSatisfiedIcon className={styles.disSatisified} />
                            ) : null}

                            <p
                              className={clsx(
                                styles.commentContent,
                                content.type === 'neutral' && styles.neutral,
                              )}
                              dangerouslySetInnerHTML={{
                                __html: `${highlightText(content.comment)}`,
                              }}
                            ></p>
                          </div>
                        );
                      })
                    ) : (
                      <small className={clsx(styles.comment, styles.noComment)}>
                        {translations.no_comment_review}
                      </small>
                    )}

                    <a href={review.reviewUrl} target='_blank' rel='noreferrer'>
                      {translations.reviewed_from}&nbsp;
                      <img
                        className={styles.providerLogo}
                        src={cloudinaryImageTransformations(
                          `https://assets.wego.com/image/upload/v1730782126/web/review_provider/${
                            review.providerCode === 'booking.com' ? review.providerCode : 'expedia'
                          }.png`,
                          'c_fit',
                          'h_210',
                          'f_auto',
                          'q_auto',
                        )}
                      />
                    </a>
                  </div>

                  <small className={styles.reviewPostedDate}>
                    {postedDateDisplay(translations, new Date(review.postedAt), locale)}
                  </small>
                </div>
              </div>
            </div>
          ))}

          <div className={styles.paginationBar}>
            <div
              className={clsx(styles.navigator, currentPage === 1 && styles.disabled)}
              onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)}
            >
              <ChevronLeftIcon />
            </div>

            {pager(currentPage, guestReviews.meta.pagination.totalPages).map((page, index) => {
              return (
                <div
                  key={`${page}-${index}`}
                  className={clsx(
                    styles.navigator,
                    typeof page !== 'number' && styles.disabled,
                    currentPage === page && styles.active,
                  )}
                  onClick={() => typeof page === 'number' && setCurrentPage(page)}
                >
                  {page}
                </div>
              );
            })}

            <div
              className={clsx(
                styles.navigator,
                currentPage === guestReviews.meta.pagination.totalPages && styles.disabled,
              )}
              onClick={() =>
                currentPage < guestReviews.meta.pagination.totalPages &&
                setCurrentPage(currentPage + 1)
              }
            >
              <ChevronRightIcon />
            </div>
          </div>
        </>
      ) : (
        <div className={styles.noResults}>
          <EmptyReviewsIcon className={styles.icon} />
          <strong>{translations.no_results_header_v2}</strong>
          <div className={styles.message}>{translations.try_searching_other_keyword}</div>
        </div>
      )}
    </div>
  );
};

export default GuestReviews;
